import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ScheduleReportModal = ({ isOpen, onClose, selectedReport, onSchedule }) => {
  const [scheduleData, setScheduleData] = useState({
    reportName: '',
    frequency: 'weekly',
    dayOfWeek: 'monday',
    dayOfMonth: '1',
    time: '09:00',
    recipients: [''],
    format: 'pdf',
    includeCharts: true,
    includeData: true,
    customMessage: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' }
  ];

  const dayOfWeekOptions = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  const formatOptions = [
    { value: 'pdf', label: 'PDF Document' },
    { value: 'excel', label: 'Excel Spreadsheet' },
    { value: 'csv', label: 'CSV File' }
  ];

  const handleInputChange = (field, value) => {
    setScheduleData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleRecipientsChange = (index, value) => {
    const newRecipients = [...scheduleData.recipients];
    newRecipients[index] = value;
    setScheduleData(prev => ({
      ...prev,
      recipients: newRecipients
    }));
  };

  const addRecipient = () => {
    setScheduleData(prev => ({
      ...prev,
      recipients: [...prev.recipients, '']
    }));
  };

  const removeRecipient = (index) => {
    if (scheduleData.recipients.length > 1) {
      const newRecipients = scheduleData.recipients.filter((_, i) => i !== index);
      setScheduleData(prev => ({
        ...prev,
        recipients: newRecipients
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!scheduleData.reportName.trim()) {
      newErrors.reportName = 'Report name is required';
    }

    const validRecipients = scheduleData.recipients.filter(email => email.trim());
    if (validRecipients.length === 0) {
      newErrors.recipients = 'At least one recipient is required';
    } else {
      const invalidEmails = validRecipients.filter(email => !/\S+@\S+\.\S+/.test(email));
      if (invalidEmails.length > 0) {
        newErrors.recipients = 'Please enter valid email addresses';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const scheduleInfo = {
        ...scheduleData,
        id: `SCHEDULE-${Date.now()}`,
        reportId: selectedReport?.reportId,
        reportCategory: selectedReport?.categoryId,
        createdAt: new Date().toISOString(),
        status: 'active',
        nextRun: calculateNextRun()
      };

      onSchedule(scheduleInfo);
      handleClose();
    } catch (error) {
      console.error('Failed to schedule report:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateNextRun = () => {
    const now = new Date();
    const [hours, minutes] = scheduleData.time.split(':').map(Number);
    
    let nextRun = new Date(now);
    nextRun.setHours(hours, minutes, 0, 0);

    switch (scheduleData.frequency) {
      case 'daily':
        if (nextRun <= now) {
          nextRun.setDate(nextRun.getDate() + 1);
        }
        break;
      case 'weekly':
        const targetDay = dayOfWeekOptions.findIndex(day => day.value === scheduleData.dayOfWeek);
        const currentDay = nextRun.getDay();
        const daysUntilTarget = (targetDay - currentDay + 7) % 7;
        nextRun.setDate(nextRun.getDate() + (daysUntilTarget || 7));
        break;
      case 'monthly':
        nextRun.setDate(parseInt(scheduleData.dayOfMonth));
        if (nextRun <= now) {
          nextRun.setMonth(nextRun.getMonth() + 1);
        }
        break;
      case 'quarterly':
        nextRun.setDate(parseInt(scheduleData.dayOfMonth));
        nextRun.setMonth(Math.floor(nextRun.getMonth() / 3) * 3);
        if (nextRun <= now) {
          nextRun.setMonth(nextRun.getMonth() + 3);
        }
        break;
    }

    return nextRun.toISOString();
  };

  const handleClose = () => {
    setScheduleData({
      reportName: '',
      frequency: 'weekly',
      dayOfWeek: 'monday',
      dayOfMonth: '1',
      time: '09:00',
      recipients: [''],
      format: 'pdf',
      includeCharts: true,
      includeData: true,
      customMessage: ''
    });
    setErrors({});
    setIsSubmitting(false);
    onClose();
  };

  const getReportTitle = () => {
    if (!selectedReport) return 'Unknown Report';
    
    const reportTitles = {
      'card-activity': 'Card Activity Summary',
      'card-performance': 'Card Performance Analysis',
      'card-lifecycle': 'Card Lifecycle Report',
      'card-geographic': 'Geographic Usage Distribution',
      'payment-summary': 'Payment Summary',
      'revenue-analysis': 'Revenue Analysis',
      'payment-methods': 'Payment Methods Report',
      'failed-payments': 'Failed Payments Analysis',
      'user-engagement': 'User Engagement Metrics',
      'user-growth': 'User Growth Analysis',
      'team-performance': 'Team Performance Report',
      'user-retention': 'User Retention Analysis',
      'billing-overview': 'Billing Overview',
      'invoice-analysis': 'Invoice Analysis',
      'subscription-metrics': 'Subscription Metrics',
      'billing-forecasting': 'Billing Forecasting'
    };

    return reportTitles[selectedReport.reportId] || 'Report';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Schedule Report Delivery"
      size="lg"
      closeOnOverlayClick={false}
    >
      <div className="p-6">
        {/* Report Info */}
        <div className="bg-surface border border-border rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <Icon name="BarChart3" size={20} className="text-accent" />
            <div>
              <h3 className="font-medium text-text-primary">{getReportTitle()}</h3>
              <p className="text-sm text-text-muted">Automated delivery schedule</p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Basic Settings */}
          <div>
            <h4 className="text-lg font-medium text-text-primary mb-4">Basic Settings</h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Schedule Name *
                </label>
                <Input
                  type="text"
                  placeholder="Enter schedule name"
                  value={scheduleData.reportName}
                  onChange={(e) => handleInputChange('reportName', e.target.value)}
                  className={errors.reportName ? 'border-error' : ''}
                />
                {errors.reportName && (
                  <p className="text-error text-sm mt-1">{errors.reportName}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Frequency
                  </label>
                  <select
                    value={scheduleData.frequency}
                    onChange={(e) => handleInputChange('frequency', e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  >
                    {frequencyOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Time
                  </label>
                  <Input
                    type="time"
                    value={scheduleData.time}
                    onChange={(e) => handleInputChange('time', e.target.value)}
                  />
                </div>
              </div>

              {scheduleData.frequency === 'weekly' && (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Day of Week
                  </label>
                  <select
                    value={scheduleData.dayOfWeek}
                    onChange={(e) => handleInputChange('dayOfWeek', e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  >
                    {dayOfWeekOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {(scheduleData.frequency === 'monthly' || scheduleData.frequency === 'quarterly') && (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Day of Month
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="28"
                    value={scheduleData.dayOfMonth}
                    onChange={(e) => handleInputChange('dayOfMonth', e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Recipients */}
          <div>
            <h4 className="text-lg font-medium text-text-primary mb-4">Recipients</h4>
            
            <div className="space-y-3">
              {scheduleData.recipients.map((recipient, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    value={recipient}
                    onChange={(e) => handleRecipientsChange(index, e.target.value)}
                    className={`flex-1 ${errors.recipients ? 'border-error' : ''}`}
                  />
                  {scheduleData.recipients.length > 1 && (
                    <Button
                      variant="ghost"
                      onClick={() => removeRecipient(index)}
                      className="p-2"
                    >
                      <Icon name="X" size={16} className="text-error" />
                    </Button>
                  )}
                </div>
              ))}
              
              <Button
                variant="ghost"
                onClick={addRecipient}
                iconName="Plus"
                iconPosition="left"
                className="text-accent"
              >
                Add Recipient
              </Button>
              
              {errors.recipients && (
                <p className="text-error text-sm">{errors.recipients}</p>
              )}
            </div>
          </div>

          {/* Format & Content */}
          <div>
            <h4 className="text-lg font-medium text-text-primary mb-4">Format & Content</h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Export Format
                </label>
                <select
                  value={scheduleData.format}
                  onChange={(e) => handleInputChange('format', e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                >
                  {formatOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={scheduleData.includeCharts}
                    onChange={(e) => handleInputChange('includeCharts', e.target.checked)}
                    className="rounded border-border text-accent focus:ring-accent"
                  />
                  <span className="text-sm text-text-primary">Include charts and visualizations</span>
                </label>
                
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={scheduleData.includeData}
                    onChange={(e) => handleInputChange('includeData', e.target.checked)}
                    className="rounded border-border text-accent focus:ring-accent"
                  />
                  <span className="text-sm text-text-primary">Include raw data tables</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Custom Message (Optional)
                </label>
                <textarea
                  placeholder="Add a custom message to include with the report..."
                  value={scheduleData.customMessage}
                  onChange={(e) => handleInputChange('customMessage', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-6 border-t border-border">
          <div className="text-sm text-text-muted">
            Next delivery: {calculateNextRun() ? new Date(calculateNextRun()).toLocaleString() : 'Not scheduled'}
          </div>
          
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {isSubmitting ? 'Scheduling...' : 'Schedule Report'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ScheduleReportModal;